import { cloneDeep, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { ScheduleInput, SummaryInput } from 'sr-types/lib/production/v1/graphql';
import Loading from '../common/components/Loading';
import { StepsIndexConstant } from '../common/components/Stepper';
import { useStepper } from '../common/components/StepperContext';
import { STATE_CONSTANTS } from '../common/constants/constants';
import { FormProvider } from '../common/form/FormContext';
import FormModalInfoAlert from '../common/form/FormModalInfoAlert';
import { EMPTY_SCHEDULE } from '../common/form/widgets/ScheduleWidget';
import { useBrowserOrAccountLocale } from '../common/hooks/useBrowserOrAccountLocale';
import { ValidationRulesType } from '../production/helpers/productionUtils';
import { RESOURCE_TYPE } from '../resource/common/resourceConstants';
import { resourceFormValidationRules } from '../resource/resourceUtils';
import { getDateRangeListeners } from '../supply/booking/ScheduleCalculator';
import StepResource from './StepResource';

export const EMPTY_RESOURCE_VALUES = {
    name: '',
    id: '',
    type: '',
    role: null,
    status: null,
    budgetCategory: null,
    resourceCategory: null,
    resourceSchedule: {
        autoSetDate: false,
        dateRange: { ...EMPTY_SCHEDULE }
    },
    summary: {
        about: ''
    },
    locations: []
};

export type ResourceFormValues = {
    name: string;
    type: string;
    role: string;
    status: string;
    budgetCategory: string;
    resourceCategory: string;
    resourceSchedule: ScheduleInput;
    summary: SummaryInput;
    locations: any[];
};

export type CreateResourceType = {
    resType: string;
    formAlertTitleToken?: string;
    formAlertDescriptionToken?: string;
};

export default (props: CreateResourceType) => {
    const { resType, formAlertTitleToken, formAlertDescriptionToken } = props;
    const { getStepData, activeStep } = useStepper();
    const title = 'stepper.hiring.resource.info.title';

    let selectedType = resType;
    let descriptionToken = 'stepper.hiring.resource.info.crew.description';
    if (resType === RESOURCE_TYPE.supplier) {
        descriptionToken = 'stepper.hiring.resource.info.supplier.description';
        selectedType = getStepData(StepsIndexConstant.zero)?.type;
    }
    const [initialState, setInitialState] = useState<ResourceFormValues>({
        ...cloneDeep(EMPTY_RESOURCE_VALUES),
        type: selectedType,
        status: STATE_CONSTANTS.open
    });
    const locale = useBrowserOrAccountLocale();

    useEffect(() => {
        const stepVal = getStepData(activeStep);
        if (stepVal) {
            setInitialState({ ...stepVal });
        }
    }, []);

    const validationRules: ValidationRulesType = useMemo(() => {
        return resourceFormValidationRules(true, [RESOURCE_TYPE.cast, RESOURCE_TYPE.crew].includes(initialState?.type));
    }, [initialState]);

    return isEmpty(initialState) ? (
        <Loading />
    ) : (
        <>
            <FormModalInfoAlert
                title={formAlertTitleToken ?? title}
                descriptionToken={formAlertDescriptionToken ?? descriptionToken}
            />
            <FormProvider
                state={initialState}
                setState={setInitialState}
                validationRules={validationRules}
                listeners={[...getDateRangeListeners('resourceSchedule.dateRange', locale)]}
                disableFunctionalStateUpdate
            >
                <StepResource key={resType} />
            </FormProvider>
        </>
    );
};
